import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  format, startOfMonth, getDaysInMonth,
  addMonths, subMonths,
} from "date-fns";

export default function useFinanceData() {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const { data: sales = [] } = useQuery({
    queryKey: ["sales"],
    queryFn: () => base44.entities.Sale.list("-date"),
    initialData: [],
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses"],
    queryFn: () => base44.entities.Expense.list("-date"),
    initialData: [],
  });

  const { data: shifts = [] } = useQuery({
    queryKey: ["shifts"],
    queryFn: () => base44.entities.Shift.list(),
    initialData: [],
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: () => base44.entities.Employee.list(),
    initialData: [],
  });

  const { data: restaurant } = useQuery({
    queryKey: ["restaurant"],
    queryFn: async () => {
      const data = await base44.entities.Restaurant.list();
      return data[0];
    },
  });

  const now = useMemo(() => new Date(), []);
  const currentMonth = selectedMonth.getMonth();
  const currentYear = selectedMonth.getFullYear();
  const daysInMonth = getDaysInMonth(selectedMonth);
  const currentDayOfMonth = now.getDate();

  const isCurrentMonth = now.getMonth() === currentMonth && now.getFullYear() === currentYear;
  const isFutureMonth = selectedMonth > now;
  const isPastMonth = selectedMonth < startOfMonth(now);

  // Opening days based on restaurant settings
  const { totalOpenDays, remainingOpenDays } = useMemo(() => {
    if (!restaurant || !restaurant.opening_days) {
      return {
        totalOpenDays: daysInMonth,
        remainingOpenDays: daysInMonth - currentDayOfMonth,
      };
    }

    const dayNames = [
      t("setup.sunday"), t("setup.monday"), t("setup.tuesday"),
      t("setup.wednesday"), t("setup.thursday"), t("setup.friday"),
      t("setup.saturday"),
    ];
    let total = 0;
    let remaining = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      if (restaurant.opening_days.includes(dayNames[date.getDay()])) {
        total++;
        if (day > currentDayOfMonth) remaining++;
      }
    }

    return { totalOpenDays: total, remainingOpenDays: remaining };
  }, [restaurant, daysInMonth, currentDayOfMonth, currentYear, currentMonth, t]);

  const openDaysPassed = isFutureMonth ? 0 : (isPastMonth ? totalOpenDays : totalOpenDays - remainingOpenDays);

  // Filter sales/expenses by month
  const currentMonthSales = useMemo(
    () => sales.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [sales, currentMonth, currentYear]
  );

  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  const lastMonthSales = useMemo(
    () => sales.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    }),
    [sales, lastMonth, lastMonthYear]
  );

  const currentMonthExpenses = useMemo(
    () => expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [expenses, currentMonth, currentYear]
  );

  // Revenue totals
  const currentMonthRevenue = useMemo(
    () => currentMonthSales.reduce((sum, s) => sum + (s.total_amount || 0), 0),
    [currentMonthSales]
  );
  const lastMonthRevenue = useMemo(
    () => lastMonthSales.reduce((sum, s) => sum + (s.total_amount || 0), 0),
    [lastMonthSales]
  );
  const currentMonthGuests = useMemo(
    () => currentMonthSales.reduce((sum, s) => sum + (s.guests_count || 0), 0),
    [currentMonthSales]
  );

  // COGS from Wareneinkauf
  const currentMonthCogs = useMemo(
    () => currentMonthExpenses
      .filter(e => e.category === "Wareneinkauf")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [currentMonthExpenses]
  );

  const lastMonthCogs = useMemo(() => {
    const lastExpenses = expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });
    return lastExpenses
      .filter(e => e.category === "Wareneinkauf")
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [expenses, lastMonth, lastMonthYear]);

  // Historical COGS rate for forecasting
  const historicalCogsRate = useMemo(() => {
    if (currentMonthRevenue > 0 && currentMonthCogs > 0) return currentMonthCogs / currentMonthRevenue;
    if (lastMonthRevenue > 0 && lastMonthCogs > 0) return lastMonthCogs / lastMonthRevenue;
    return 0.30;
  }, [currentMonthRevenue, currentMonthCogs, lastMonthRevenue, lastMonthCogs]);

  // Labor costs from shifts
  const currentMonthShifts = useMemo(
    () => shifts.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [shifts, currentMonth, currentYear]
  );

  const calcShiftHours = (shift, employee) => {
    let hours = 8;
    if (shift.shift_type === "Mittag" && employee.shift_start_lunch && employee.shift_end_lunch) {
      const [sH, sM] = employee.shift_start_lunch.split(":").map(Number);
      const [eH, eM] = employee.shift_end_lunch.split(":").map(Number);
      hours = (eH + eM / 60) - (sH + sM / 60);
    } else if (shift.shift_type === "Abend" && employee.shift_start_dinner && employee.shift_end_dinner) {
      const [sH, sM] = employee.shift_start_dinner.split(":").map(Number);
      const [eH, eM] = employee.shift_end_dinner.split(":").map(Number);
      hours = (eH + eM / 60) - (sH + sM / 60);
    }
    return hours;
  };

  const currentMonthLaborCost = useMemo(() => {
    let total = 0;
    currentMonthShifts.forEach(shift => {
      const emp = employees.find(e => e.id === shift.employee_id);
      if (emp?.hourly_rate) {
        total += emp.hourly_rate * calcShiftHours(shift, emp);
      }
    });
    return total;
  }, [currentMonthShifts, employees]);

  // Other expenses (non-COGS, non-Personal)
  const currentMonthOtherExpenses = useMemo(
    () => currentMonthExpenses
      .filter(e => e.category !== "Wareneinkauf" && e.category !== "Personal")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [currentMonthExpenses]
  );

  // Expense projections (recurring vs variable)
  const expenseProjection = useMemo(() => {
    const other = currentMonthExpenses.filter(
      e => e.category !== "Wareneinkauf" && e.category !== "Personal"
    );
    const monthlyRecurring = other
      .filter(e => e.is_recurring && e.recurring_period === "Monatlich")
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const variable = other
      .filter(e => !(e.is_recurring && e.recurring_period === "Monatlich"))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const avgVarPerDay = openDaysPassed > 0 ? variable / openDaysPassed : 0;
    const projectedRemainingVar = avgVarPerDay * remainingOpenDays;

    return {
      currentMonthly: monthlyRecurring,
      currentVariable: variable,
      projectedRemainingVariable: projectedRemainingVar,
      projectedTotalOther: monthlyRecurring + variable + projectedRemainingVar,
    };
  }, [currentMonthExpenses, openDaysPassed, remainingOpenDays]);

  // Averages
  const avgRevenuePerDay = isFutureMonth
    ? 0 // simplified: no forecast for future months
    : (openDaysPassed > 0 ? currentMonthRevenue / openDaysPassed : 0);

  const avgLaborPerDay = openDaysPassed > 0 ? currentMonthLaborCost / openDaysPassed : 0;

  // Projected labor (planned shifts + avg for unplanned)
  const projectedTotalLabor = useMemo(() => {
    const dayNames = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

    const futureDays = [];
    for (let day = currentDayOfMonth + 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayName = dayNames[date.getDay()];
      if (!restaurant?.opening_days || restaurant.opening_days.includes(dayName)) {
        futureDays.push(format(date, "yyyy-MM-dd"));
      }
    }

    let plannedCost = 0;
    let plannedDays = 0;
    futureDays.forEach(dateStr => {
      const dayShifts = shifts.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      if (dayShifts.length > 0) {
        plannedDays++;
        dayShifts.forEach(shift => {
          const emp = employees.find(e => e.id === shift.employee_id);
          if (emp?.hourly_rate) {
            plannedCost += emp.hourly_rate * calcShiftHours(shift, emp);
          }
        });
      }
    });

    const unplannedDays = remainingOpenDays - plannedDays;
    const unplannedCost = unplannedDays > 0 ? avgLaborPerDay * unplannedDays : 0;
    return currentMonthLaborCost + plannedCost + unplannedCost;
  }, [currentMonthLaborCost, shifts, employees, restaurant, currentDayOfMonth, daysInMonth, currentYear, currentMonth, remainingOpenDays, avgLaborPerDay]);

  // Projections
  const projectedRemainingRevenue = avgRevenuePerDay * remainingOpenDays;
  const projectedTotalRevenue = currentMonthRevenue + projectedRemainingRevenue;
  const projectedTotalCogs = currentMonthCogs + (projectedRemainingRevenue * historicalCogsRate);
  const projectedTotalProfit = projectedTotalRevenue - projectedTotalCogs - projectedTotalLabor - expenseProjection.projectedTotalOther;
  const projectedProfitMargin = projectedTotalRevenue > 0
    ? (projectedTotalProfit / projectedTotalRevenue) * 100
    : 0;

  // Current actuals
  const totalExpenses = currentMonthCogs + currentMonthLaborCost + currentMonthOtherExpenses;
  const currentProfit = currentMonthRevenue - totalExpenses;
  const currentProfitMargin = currentMonthRevenue > 0
    ? (currentProfit / currentMonthRevenue) * 100
    : 0;

  // MoM
  const revenueMoM = lastMonthRevenue > 0
    ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
    : 0;

  // Daily chart data
  const dailyData = useMemo(() => {
    const data = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = format(new Date(currentYear, currentMonth, day), "yyyy-MM-dd");
      const daySales = currentMonthSales.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      const dayRevenue = daySales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const dayGuests = daySales.reduce((sum, s) => sum + (s.guests_count || 0), 0);

      const dayExpenses = currentMonthExpenses.filter(e => format(new Date(e.date), "yyyy-MM-dd") === dateStr);
      const dayCogs = dayExpenses
        .filter(e => e.category === "Wareneinkauf")
        .reduce((sum, e) => sum + (e.amount || 0), 0);
      const dayOther = dayExpenses
        .filter(e => e.category !== "Wareneinkauf" && e.category !== "Personal")
        .reduce((sum, e) => sum + (e.amount || 0), 0);

      const dayShifts = currentMonthShifts.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      let dayLabor = 0;
      const shiftDetails = [];
      dayShifts.forEach(shift => {
        const emp = employees.find(e => e.id === shift.employee_id);
        if (emp?.hourly_rate) {
          const hours = calcShiftHours(shift, emp);
          const cost = emp.hourly_rate * hours;
          dayLabor += cost;
          shiftDetails.push({
            employee: emp.name,
            shift: shift.shift_type,
            hours: hours.toFixed(1),
            rate: emp.hourly_rate,
            cost: cost.toFixed(2),
          });
        }
      });

      const dailyFixed = totalOpenDays > 0 ? expenseProjection.currentMonthly / totalOpenDays : 0;
      const actualOrEstCogs = dayCogs > 0 ? dayCogs : dayRevenue * historicalCogsRate;
      const totalDayCosts = actualOrEstCogs + dayLabor + dayOther + dailyFixed;

      data.push({
        day,
        date: dateStr,
        revenue: dayRevenue,
        guests: dayGuests,
        costs: totalDayCosts,
        profit: dayRevenue - totalDayCosts,
        cogs: actualOrEstCogs,
        laborCost: dayLabor,
        otherExpenses: dayOther,
        fixedCosts: dailyFixed,
        shifts: shiftDetails,
        expenseDetails: dayExpenses,
        isFuture: new Date(currentYear, currentMonth, day) > now,
      });
    }
    return data;
  }, [currentMonthSales, currentMonthExpenses, currentMonthShifts, employees, daysInMonth, currentYear, currentMonth, totalOpenDays, expenseProjection.currentMonthly, historicalCogsRate, now]);

  return {
    // Month nav
    selectedMonth,
    setSelectedMonth,
    prevMonth: () => setSelectedMonth(subMonths(selectedMonth, 1)),
    nextMonth: () => setSelectedMonth(addMonths(selectedMonth, 1)),
    isCurrentMonth,
    isFutureMonth,
    isPastMonth,

    // Raw data (for expense CRUD)
    expenses,
    sales,

    // Current month actuals
    revenue: currentMonthRevenue,
    totalExpenses,
    profit: currentProfit,
    profitMargin: currentProfitMargin,
    guests: currentMonthGuests,

    // MoM
    revenueMoM,
    lastMonthRevenue,

    // Forecast
    projectedRevenue: projectedTotalRevenue,
    projectedProfit: projectedTotalProfit,
    projectedProfitMargin,

    // Opening days
    totalOpenDays,
    openDaysPassed,
    remainingOpenDays,

    // Chart
    dailyData,

    // For day detail dialog
    historicalCogsRate,
    historicalLaborRate: currentMonthRevenue > 0 && currentMonthLaborCost > 0
      ? currentMonthLaborCost / currentMonthRevenue
      : 0.25,
  };
}
